// src/pages/ReverifyID.tsx
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { authFetch } from "../lib/auth";
import { authNext } from "../lib/authNavigation";
import { isVerificationExempt, verificationNext } from "../lib/verification";
import { useVerificationStatus } from "../hooks/useVerificationStatus";
import VerificationStatusNotice from "../components/design/VerificationStatusNotice";
import PaymentStepFrame from "../components/design/PaymentStepFrame";
import { verificationRequestContext } from "../lib/verificationNavigation";

export default function ReverifyID() {
  const [params] = useSearchParams();
  const { account, loading, error, retry } = useVerificationStatus();
  const returnTo = verificationNext(authNext(params.get("returnTo")) || params.get("next"), "/contracts/new");
  if (loading || error || !account) return <VerificationStatusNotice error={error} retry={retry} />;
  if (isVerificationExempt(account)) return <Navigate to={returnTo} replace />;
  return <ReverifyIDForm key={account.id} returnTo={returnTo} />;
}

function ReverifyIDForm({ returnTo }: { returnTo: string }) {
  const navigate = useNavigate();
  const { amountEntry } = verificationRequestContext(returnTo);

  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const submitting = useRef(false);
  const finished = useRef(false);
  const mounted = useRef(true);
  const redirectTimer = useRef<ReturnType<typeof window.setTimeout> | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (redirectTimer.current !== null) window.clearTimeout(redirectTimer.current);
    };
  }, []);

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (submitting.current || finished.current) return;
    setErr(null);
    setMessage(null);
    const f = e.target.files?.[0] || null;
    setFile(f);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting.current || finished.current) return;
    if (!file) {
      setErr("신분증 이미지를 선택해 주세요.");
      return;
    }

    setErr(null);
    setMessage(null);
    submitting.current = true;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("idcard", file);

      const res = await authFetch("/api/ekyc/idcard", {
        method: "POST",
        body: fd,
      });

      const json = await res.json().catch(() => ({}));
      if (!mounted.current) return;

      if (!res.ok) {
        throw new Error(json.message || "신분증 인증에 실패했습니다.");
      }

      finished.current = true;
      setMessage("인증 완료 · 이전 단계로 이동합니다.");

      const baseDest = returnTo;

      redirectTimer.current = window.setTimeout(() => {
        try {
          const url = new URL(baseDest, window.location.origin);
          url.searchParams.set("session_ekyc", "ok");
          navigate(url.pathname + url.search + url.hash, { replace: true });
        } catch {
          const sep = baseDest.includes("?") ? "&" : "?";
          navigate(baseDest + sep + "session_ekyc=ok", { replace: true });
        }
      }, 1500);
    } catch (e: any) {
      console.error("/api/ekyc/idcard error", e);
      if (mounted.current) setErr(e?.message || "신분증 인증 중 오류가 발생했습니다.");
    } finally {
      submitting.current = false;
      if (mounted.current) setLoading(false);
    }
  };

  const handleBack = () => {
    if (submitting.current || finished.current) return;
    const dest = returnTo || "/";
    navigate(dest, { replace: true });
  };

  return (
    <div className="jp-payment-page">
      <PaymentStepFrame step={2} steps={amountEntry ? ["송금 금액", "정보·서류", "최종 확인"] : ["결제 정보", "본인 확인", "받는 분 정보"]} title="신분증을 등록해 주세요" description="주민등록증 · 운전면허증 이미지" onBack={handleBack} busy={loading || !!message}>
        <form onSubmit={onSubmit} aria-busy={loading}>
          <label className="mt-4 block rounded-xl border border-dashed border-stone-300 bg-stone-50 p-4">
            <span className="mb-2 block text-sm font-semibold text-slate-800">신분증 이미지 선택</span>
            <input
              type="file"
              accept="image/*"
              disabled={loading || !!message}
              onChange={onFileChange}
              className="block w-full text-sm text-slate-700 file:mr-4 file:rounded-lg file:border file:border-stone-200 file:bg-white file:px-4 file:py-3 file:text-sm file:font-semibold file:text-stone-700 hover:file:bg-orange-50"
            />
            {file ? <div className="mt-3 break-all text-xs font-semibold text-orange-700">선택된 파일: {file.name}</div> : null}
          </label>

          {err ? (
            <div role="alert" className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{err}</div>
          ) : null}

          {message ? (
            <div role="status" className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{message}</div>
          ) : null}

          <div className="mt-5 grid">
            <button
              type="submit"
              disabled={loading || !file || !!message}
              className="inline-flex items-center justify-center rounded-lg bg-[#f97316] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#ea580c] disabled:cursor-not-allowed disabled:bg-orange-200"
            >
              {loading ? "인증 진행 중..." : "신분증 인증 요청"}
            </button>
          </div>
        </form>
      </PaymentStepFrame>
    </div>
  );
}
